// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);

// Helper function to check if token is still valid
const isTokenValid = (authUser) => {
  if (!authUser || !authUser.token) {
    console.log("❌ No authUser or token found");
    return false;
  }

  // Check if user has required fields
  if (!authUser.username || !authUser.customerId) {
    console.log("❌ Missing required user fields");
    return false;
  }

  // For string tokens (login API), simple check
  if (typeof authUser.token === 'string') {
    const hasValidToken = authUser.token.length > 0 && authUser.token !== 'dummy-token-123';
    console.log("🔍 String token validation:", hasValidToken);
    return hasValidToken;
  }

  // For object tokens (MSAL), check access_token
  if (authUser.token && authUser.token.access_token) {
    const hasValidToken = authUser.token.access_token.length > 0;
    console.log("🔍 MSAL token validation:", hasValidToken);
    return hasValidToken;
  }

  // Check for token expiration if expiresOn field exists
  if (authUser.token.expiresOn) {
    const expiryTime = new Date(authUser.token.expiresOn);
    const now = new Date();
    const isExpired = expiryTime <= now;
    console.log("🔍 Token expiry check:", { expiryTime, now, isExpired });
    return !isExpired;
  }

  console.log("❌ Token format not recognized");
  return false;
};

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    const initializeAuth = () => {
      try {
        console.log("🔄 Initializing authentication state...");
        
        // Check if we're on a protected route (not root)
        const isOnProtectedRoute = window.location.pathname !== '/';
        
        if (isOnProtectedRoute) {
          // Only restore session if user is trying to access a protected route
          const storedAuthUser = localStorage.getItem("authUser");
          
          if (storedAuthUser) {
            const parsedAuthUser = JSON.parse(storedAuthUser);
            
            // Only restore if token appears valid
            if (isTokenValid(parsedAuthUser)) {
              console.log("🔄 Restoring valid session from localStorage for protected route");
              setAuthUser(parsedAuthUser);
            } else {
              console.log("❌ Invalid token found, clearing localStorage");
              localStorage.removeItem("authUser");
              localStorage.removeItem("authToken");
            }
          } else {
            console.log("ℹ️ No stored session found - redirecting to login");
          }
        } else {
          console.log("ℹ️ On login page - not restoring session automatically");
          // Clear any existing session when visiting login page
          const storedAuthUser = localStorage.getItem("authUser");
          if (storedAuthUser) {
            console.log("🗑️ Clearing existing session for fresh login");
            localStorage.removeItem("authUser");
            localStorage.removeItem("authToken");
          }
        }
      } catch (error) {
        console.error("❌ Error initializing auth state:", error);
        // Clear corrupted data
        localStorage.removeItem("authUser");
        localStorage.removeItem("authToken");
      } finally {
        setIsInitialized(true);
        console.log("✅ Authentication initialization complete");
      }
    };

    initializeAuth();
  }, []);

  // 🔐 Login handler
  const login = (userData) => {
    console.log("🔐 Login successful, storing user data");
    
    // If there's a token, decode it to get roles
    if (userData.token && userData.token.access_token) {
      debugger;
      try {
        const tokenParts = userData.token.access_token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          // Add roles from token to userData
          userData.roles = payload.roles || [];
          userData.isSuperAdmin = payload.roles?.includes('superadmin') || false;
          
          // Add debug logging for role information
          console.log('🔑 Token roles:', payload.roles);
          console.log('👑 Is SuperAdmin:', userData.isSuperAdmin);
        }
      } catch (error) {
        console.error("Error decoding JWT token:", error);
      }
    }
    
    localStorage.setItem("authUser", JSON.stringify(userData));
    setAuthUser(userData);
  };

  // 🔓 Logout handler
  const logout = async () => {
    console.log("🔓 Logging out, clearing all data");
    
    // Clear local state first
    setAuthUser(null);
    
    // Clear all authentication-related localStorage keys
    const authKeys = [
      "authUser",
      "authToken", 
      "username",
      "isSuperAdmin",
      "isValidUser"
    ];

    // Clear application-specific localStorage keys (optional - for clean slate)
    const appKeys = [
      "selectedFile",
      "selectedInstance", 
      "integrationType",
      "uploadedFiles",
      "fileDataMap"
    ];
    
    // Remove authentication keys
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed ${key} from localStorage`);
    });

    // Optionally remove application-specific keys for a clean slate
    appKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`🗑️ Removed application key ${key} from localStorage`);
      }
    });

    // Clear sessionStorage as well (in case anything is stored there)
    try {
      sessionStorage.clear();
      console.log("🗑️ Cleared sessionStorage");
    } catch (error) {
      console.warn("⚠️ Could not clear sessionStorage:", error);
    }
    
    console.log("✅ All authentication data cleared successfully");
    // Note: MSAL session clearing is handled by clearAllSessions() in header component
  };

  // Don't render children until auth state is initialized
  if (!isInitialized) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ authUser, login, logout, isInitialized }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
